import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
    Portal,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    FormControlLabel,
    TextField,
    IconButton,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    getReferenceLastDigits,
    DEFAULT_REFERENCE_LAST_DIGITS,
} from '../../Utils/matchingUtils.js';
import { isDuplicateDescription as isDuplicateDescriptionText } from '../../Utils/documentDescriptionFlags.js';
import CircularProgress from '@mui/material/CircularProgress';
import fieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import styles from './DuplicateEntriesModal.module.scss';

const PRIMARY = 'var(--xtram-blue)';
const MIN_LAST_DIGITS = 1;
const MAX_LAST_DIGITS = 20;

/** Normalize a date value to a date-only key (YYYY-MM-DD), ignoring time & timezone noise. */
const normalizeDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** Normalize an amount to a stable string so 100 and 100.00 compare equal. */
const normalizeAmount = (value) => {
    const n = Number(value ?? 0);
    if (isNaN(n)) return String(value ?? '');
    return n.toFixed(2);
};

const normalizeText = (value) => (value ?? '').toString().trim().toLowerCase();

/**
 * Bank / Finsite references are often the same number with different leading zeros
 * ("0123" vs "123"). Compare digit-only refs as numbers; leave non-numeric refs alone.
 * When lastDigits is set, compare only the last N digits from the right (digits only).
 */
const normalizeReference = (value, lastDigits = null) => {
    if (lastDigits != null && lastDigits >= MIN_LAST_DIGITS) {
        return getReferenceLastDigits(value, lastDigits);
    }
    const trimmed = (value ?? '').toString().trim();
    if (!trimmed) return '';
    if (/^\d+$/.test(trimmed)) {
        const stripped = trimmed.replace(/^0+/, '');
        return stripped || '0';
    }
    return trimmed.toLowerCase();
};

export const MATCH_MODE = { EXACT: 'exact', SMART: 'smart' };

/** Above this, a smart group is close enough to pre-select duplicates for deletion. */
const CONFIDENT_SIMILARITY = 0.9;
const DEFAULT_DATE_TOLERANCE = 1;
const MAX_DATE_TOLERANCE = 31;
const DEFAULT_SIMILARITY = 60;

const dayStamp = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Whole days between two dates, or Infinity when either is unusable. */
const dayDistance = (a, b) => {
    const first = dayStamp(a);
    const second = dayStamp(b);
    if (first == null || second == null) return Number.POSITIVE_INFINITY;
    return Math.abs(Math.round((first - second) / 86400000));
};

/**
 * Bigram Dice coefficient. Bank descriptions for the same transaction differ by
 * abbreviations and word order between sources, which exact comparison rejects
 * and a character-overlap score handles well. Two empty descriptions carry no
 * signal either way, so they neither confirm nor block a match.
 */
const descriptionSimilarity = (a, b) => {
    const left = normalizeText(a).replace(/\s+/g, ' ');
    const right = normalizeText(b).replace(/\s+/g, ' ');
    if (!left && !right) return 1;
    if (!left || !right) return 0;
    if (left === right) return 1;
    if (left.length < 2 || right.length < 2) return 0;

    const grams = new Map();
    for (let i = 0; i < left.length - 1; i += 1) {
        const gram = left.slice(i, i + 2);
        grams.set(gram, (grams.get(gram) || 0) + 1);
    }
    let hits = 0;
    for (let i = 0; i < right.length - 1; i += 1) {
        const gram = right.slice(i, i + 2);
        const remaining = grams.get(gram) || 0;
        if (remaining > 0) {
            grams.set(gram, remaining - 1);
            hits += 1;
        }
    }
    return (2 * hits) / (left.length - 1 + right.length - 1);
};

/**
 * Finds rows that look like the same transaction imported twice from different
 * sources. The reference is ignored on purpose: it is the one field the sources
 * disagree on. Rows are bucketed by amount (which never drifts), then clustered
 * by how close their dates and descriptions are.
 */
const buildSmartGroups = (rows, dateTolerance, similarityThreshold) => {
    const buckets = new Map();
    (rows || []).forEach((row) => {
        if (!row || row.id == null) return;
        const key = normalizeAmount(row.amount);
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(row);
    });

    const groups = [];
    buckets.forEach((bucket) => {
        if (bucket.length < 2) return;
        const ordered = [...bucket].sort(
            (a, b) => (dayStamp(a.documentDate) ?? 0) - (dayStamp(b.documentDate) ?? 0)
        );

        const clusters = [];
        ordered.forEach((row) => {
            const match = clusters.find((cluster) => {
                const head = cluster.rows[0];
                return (
                    dayDistance(head.documentDate, row.documentDate) <= dateTolerance &&
                    descriptionSimilarity(head.description, row.description) >= similarityThreshold
                );
            });
            if (match) {
                match.rows.push(row);
                match.scores.push(
                    descriptionSimilarity(match.rows[0].description, row.description)
                );
            } else {
                clusters.push({ rows: [row], scores: [] });
            }
        });

        clusters.forEach((cluster) => {
            if (cluster.rows.length < 2) return;
            groups.push({
                rows: cluster.rows,
                confidence: Math.min(...cluster.scores),
            });
        });
    });
    return groups;
};

/** True when the row description marks it as a duplicate of another document. */
const isDuplicateDescription = (row) => {
    const descriptionText = (row?.description ?? '').toString();
    return (
        descriptionText.includes('כפולה ל מסמך שמספרו') ||
        descriptionText.includes('מסמך כפול למספר')
    );
};

const clampLastDigits = (value) => {
    const n = parseInt(value, 10);
    if (isNaN(n)) return DEFAULT_REFERENCE_LAST_DIGITS;
    return Math.min(MAX_LAST_DIGITS, Math.max(MIN_LAST_DIGITS, n));
};

const clampDateTolerance = (value) => {
    const n = parseInt(value, 10);
    if (isNaN(n)) return 0;
    return Math.min(MAX_DATE_TOLERANCE, Math.max(0, n));
};

const clampPercent = (value) => {
    const n = parseInt(value, 10);
    if (isNaN(n)) return 0;
    return Math.min(100, Math.max(0, n));
};

/**
 * Default selection: prefer rows marked as duplicates in the description;
 * if none in the group match, keep the first row and select the rest.
 */
const defaultShouldSelectForDeletion = (row, group, idx) => {
    const hasMarkedDuplicates = group.some(isDuplicateDescription);
    return hasMarkedDuplicates ? isDuplicateDescription(row) : idx !== 0;
};

/**
 * Reusable modal that detects duplicate rows and lets the user delete selected duplicates.
 * Duplicates default to rows sharing: documentDate, reference, amount, debitAccount, creditAccount.
 * Users can switch reference comparison to last N digits from the right (default 4).
 *
 * Props:
 *  - open, onClose
 *  - rows: array of data rows (each must have an `id`)
 *  - t, direction ('rtl' | 'ltr')
 *  - creditAccountOptions / debitAccountOptions: [{ cardId, name }] for friendly labels
 *  - onDeleteMany: async (ids: string[]) => void  (should refresh the page)
 *  - loading: boolean
 *  - getGroupKey?: (row, helpers?: { normalizeReference }) => string — override grouping;
 *      helpers.normalizeReference respects the last-digits option when enabled
 *  - shouldSelectForDeletion?: (row, group, idx) => boolean — override initial checkbox selection
 *  - columns?: [{ key, label, render?(row) }] — override table columns
 *  - subtitle?: string — override the subtitle under the title
 *  - enableSmartMatching?: boolean — offer a reference-free mode that finds the
 *      same transaction imported from two sources under different references
 *  - fetchDuplicates?: (filter?: { referenceLastDigits: number | null }) => Promise<groups>
 *      — when provided and `rows` is omitted, load duplicate groups from the API
 *      (Documents / Journal / Payment Acceptance). `referenceLastDigits` is the
 *      last-N reference filter (null = server default). BankPages continues to
 *      pass `rows` and does not use this prop.
 */
const normalizeApiGroups = (payload) => {
    const rawGroups = payload?.groups ?? payload?.Groups ?? payload ?? [];
    if (!Array.isArray(rawGroups)) return [];
    return rawGroups
        .map((g) => {
            const rows = (g?.rows ?? g?.Rows ?? []).map((r) => ({
                id: r.id ?? r.Id,
                docType: r.docType ?? r.DocType ?? null,
                documentDate: r.documentDate ?? r.DocumentDate,
                reference: r.reference ?? r.Reference,
                amount: r.amount ?? r.Amount,
                debitAccount: r.debitAccount ?? r.DebitAccount,
                creditAccount: r.creditAccount ?? r.CreditAccount,
                description: r.description ?? r.Description,
                uploadNumber: r.uploadNumber ?? r.UploadNumber,
            })).filter((r) => r.id != null);
            return {
                rows,
                confidence: g?.confidence ?? g?.Confidence ?? 1,
            };
        })
        .filter((g) => g.rows.length > 1);
};

/** Group ids by DocType for BulkDelete / BulkUpdate across mixed-type duplicate groups. */
const groupIdsByDocType = (rows, idSet, fallbackDocType = null) => {
    const byType = {};
    rows.forEach((row) => {
        if (!idSet.has(String(row.id))) return;
        const docType = row.docType || fallbackDocType;
        if (!docType) return;
        if (!byType[docType]) byType[docType] = [];
        byType[docType].push(row.id);
    });
    return byType;
};

const DuplicateEntriesModal = ({
    open,
    onClose,
    rows,
    t,
    direction = 'ltr',
    creditAccountOptions = [],
    debitAccountOptions = [],
    onDeleteMany,
    loading = false,
    getGroupKey,
    shouldSelectForDeletion,
    columns: columnsProp,
    subtitle,
    enableSmartMatching = false,
    fetchDuplicates,
}) => {
    const [useLastDigits, setUseLastDigits] = useState(false);
    const [lastDigits, setLastDigits] = useState(DEFAULT_REFERENCE_LAST_DIGITS);
    const [matchMode, setMatchMode] = useState(MATCH_MODE.EXACT);
    const [dateTolerance, setDateTolerance] = useState(DEFAULT_DATE_TOLERANCE);
    const [similarityPercent, setSimilarityPercent] = useState(DEFAULT_SIMILARITY);
    // Selection of ids to DELETE.
    const [selectedIds, setSelectedIds] = useState({});
    const [apiGroups, setApiGroups] = useState([]);
    const [apiLoading, setApiLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    // API mode when fetchDuplicates is provided and rows were not passed (BankPages always passes rows).
    const useApiMode = typeof fetchDuplicates === 'function' && rows == null;
    const clientRows = rows ?? [];
    const smartMode = !useApiMode && enableSmartMatching && matchMode === MATCH_MODE.SMART;

    const accountLabel = (options, cardId) => {
        if (!cardId) return '';
        const found = options?.find((o) => o.cardId === cardId);
        return found ? `${cardId} - ${found.name}` : String(cardId);
    };

    const resolveNormalizeReference = useCallback(
        (value) =>
            normalizeReference(value, useLastDigits ? lastDigits : null),
        [useLastDigits, lastDigits]
    );

    const resolveShouldSelect = shouldSelectForDeletion || defaultShouldSelectForDeletion;

    const fetchDuplicatesRef = useRef(fetchDuplicates);
    fetchDuplicatesRef.current = fetchDuplicates;

    useEffect(() => {
        if (!open || !useApiMode) return undefined;
        let cancelled = false;
        const timer = setTimeout(() => {
            setApiLoading(true);
            setApiError(null);
            (async () => {
                try {
                    const payload = await fetchDuplicatesRef.current({
                        referenceLastDigits: useLastDigits ? lastDigits : null,
                    });
                    if (cancelled) return;
                    setApiGroups(normalizeApiGroups(payload));
                } catch (err) {
                    if (cancelled) return;
                    console.error(err);
                    setApiError(err?.message || 'Failed to load duplicates');
                    setApiGroups([]);
                } finally {
                    if (!cancelled) setApiLoading(false);
                }
            })();
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, useApiMode, useLastDigits, lastDigits]);

    const clientDuplicateGroups = useMemo(() => {
        if (useApiMode) return [];
        if (smartMode) {
            return buildSmartGroups(clientRows, dateTolerance, similarityPercent / 100);
        }
        const helpers = { normalizeReference: resolveNormalizeReference };
        const map = new Map();
        clientRows.forEach((row) => {
            if (!row || row.id == null) return;
            const key = getGroupKey
                ? getGroupKey(row, helpers)
                : [
                    normalizeDate(row.documentDate),
                    resolveNormalizeReference(row.reference),
                    normalizeAmount(row.amount),
                    normalizeText(row.debitAccount),
                    normalizeText(row.creditAccount),
                ].join('|');
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(row);
        });
        return Array.from(map.values())
            .filter((group) => group.length > 1)
            .map((group) => ({ rows: group, confidence: 1 }));
    }, [
        useApiMode,
        clientRows,
        getGroupKey,
        resolveNormalizeReference,
        smartMode,
        dateTolerance,
        similarityPercent,
    ]);

    const duplicateGroups = useApiMode ? apiGroups : clientDuplicateGroups;

    useEffect(() => {
        if (!open) return;
        if (useApiMode && apiLoading) return;
        const initial = {};
        duplicateGroups.forEach((group) => {
            // Smart groups are only *possible* duplicates, so anything short of a
            // near-certain match starts unchecked and waits for a human decision.
            const preselect = group.confidence >= CONFIDENT_SIMILARITY;
            group.rows.forEach((row, idx) => {
                initial[row.id] = preselect && resolveShouldSelect(row, group.rows, idx);
            });
        });
        setSelectedIds(initial);
    }, [open, duplicateGroups, resolveShouldSelect, useApiMode, apiLoading]);

    const selectedCount = useMemo(
        () => Object.values(selectedIds).filter(Boolean).length,
        [selectedIds]
    );

    const totalDuplicateRows = useMemo(
        () => duplicateGroups.reduce((sum, g) => sum + g.rows.length, 0),
        [duplicateGroups]
    );

    const toggleRow = (id) => {
        setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleDelete = async () => {
        const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
        if (ids.length === 0) return;

        const idSet = new Set(ids.map(String));
        const keptIdsWithDuplicateDescription = [];
        const allRows = [];
        duplicateGroups.forEach((group) => {
            const touchesSelection = group.rows.some((r) => idSet.has(String(r.id)));
            group.rows.forEach((row) => {
                allRows.push(row);
                if (!touchesSelection) return;
                if (idSet.has(String(row.id))) return;
                if (isDuplicateDescriptionText(row.description) || isDuplicateDescription(row)) {
                    keptIdsWithDuplicateDescription.push(row.id);
                }
            });
        });

        const selectedRows = allRows.filter((r) => idSet.has(String(r.id)));
        const keptRows = allRows.filter(
            (r) => keptIdsWithDuplicateDescription.some((id) => String(id) === String(r.id))
        );
        const selectedByDocType = groupIdsByDocType(selectedRows, idSet);
        const keptByDocType = groupIdsByDocType(
            keptRows,
            new Set(keptIdsWithDuplicateDescription.map(String))
        );

        if (typeof onDeleteMany === 'function') {
            await onDeleteMany(ids, {
                keptIdsWithDuplicateDescription,
                selectedRows,
                keptRows,
                selectedByDocType,
                keptByDocType,
            });
        }
        onClose?.();
    };

    const busy = loading || apiLoading;

    const formatAmount = (value) => {
        const n = Number(value ?? 0);
        if (isNaN(n)) return value;
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const hasMixedDocTypes = useMemo(
        () => duplicateGroups.some((g) => g.rows.some((r) => r.docType)),
        [duplicateGroups]
    );

    const defaultColumns = [
        ...(hasMixedDocTypes
            ? [{ key: 'docType', label: t('DocumentType') || 'Type', render: (r) => (r.docType ? (t(r.docType) || r.docType) : '') }]
            : []),
        { key: 'documentDate', label: t('DocumentDate') || 'Document Date', render: (r) => normalizeDate(r.documentDate) },
        { key: 'reference', label: t('Reference') || 'Reference', render: (r) => r.reference || '' },
        { key: 'amount', label: t('Amount') || 'Amount', render: (r) => formatAmount(r.amount) },
        { key: 'debitAccount', label: t('Debit Account') || 'Debit Account', render: (r) => accountLabel(debitAccountOptions, r.debitAccount) },
        { key: 'creditAccount', label: t('Credit Account') || 'Credit Account', render: (r) => accountLabel(creditAccountOptions, r.creditAccount) },
        { key: 'description', label: t('Description') || 'Description', render: (r) => r.description || '' },
        { key: 'uploadNumber', label: t('DocumentNumber') || '#', render: (r) => r.uploadNumber ?? '' },
    ];

    const columns = columnsProp || defaultColumns;
    const comboInputProps = { disableUnderline: true };
    const checkboxSx = {
        color: PRIMARY,
        '&.Mui-checked': { color: PRIMARY },
    };
    const deleteCheckboxSx = {
        color: PRIMARY,
        '&.Mui-checked': { color: '#d32f2f' },
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('DuplicateEntriesTitle') || 'Duplicate Entries'}</div>
                                <div />
                            </div>
                            <p className={styles.subtitle}>
                                {subtitle ||
                                    t('DuplicateEntriesSubtitle') ||
                                    'Rows sharing the same date, reference, amount, debit and credit account. Deselect the rows you want to keep.'}
                            </p>

                            <div className={styles.scroll}>
                                <div className={styles.filters}>
                                        {!useApiMode && enableSmartMatching && (
                                            <div className={styles.tabs}>
                                                <button
                                                    type="button"
                                                    className={`${styles.tab}${matchMode === MATCH_MODE.EXACT ? ` ${styles.tabActive}` : ''}`}
                                                    onClick={() => setMatchMode(MATCH_MODE.EXACT)}
                                                >
                                                    {t('DuplicateMatchExact') || 'By reference'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.tab}${matchMode === MATCH_MODE.SMART ? ` ${styles.tabActive}` : ''}`}
                                                    onClick={() => setMatchMode(MATCH_MODE.SMART)}
                                                >
                                                    {t('DuplicateMatchSmart') || 'Smart (date + amount + description)'}
                                                </button>
                                            </div>
                                        )}

                                        {smartMode ? (
                                            <>
                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel} dir={direction}>
                                                        {t('DuplicateDateTolerance') || 'Date tolerance (days)'}
                                                    </span>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        variant="standard"
                                                        value={dateTolerance}
                                                        onChange={(e) => setDateTolerance(clampDateTolerance(e.target.value))}
                                                        inputProps={{ min: 0, max: MAX_DATE_TOLERANCE }}
                                                        className={fieldStyles.styleInput}
                                                        InputProps={comboInputProps}
                                                    />
                                                </div>
                                                <div className={`${styles.field} ${styles.fieldWide}`}>
                                                    <span className={styles.fieldLabel} dir={direction}>
                                                        {t('DuplicateDescriptionSimilarity') || 'Description similarity (%)'}
                                                    </span>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        variant="standard"
                                                        value={similarityPercent}
                                                        onChange={(e) => setSimilarityPercent(clampPercent(e.target.value))}
                                                        inputProps={{ min: 0, max: 100, step: 5 }}
                                                        className={fieldStyles.styleInput}
                                                        InputProps={comboInputProps}
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <FormControlLabel
                                                    className={styles.checkboxLabel}
                                                    control={
                                                        <Checkbox
                                                            checked={useLastDigits}
                                                            onChange={(e) => setUseLastDigits(e.target.checked)}
                                                            sx={checkboxSx}
                                                        />
                                                    }
                                                    label={
                                                        t('CompareReferenceLastDigits') ||
                                                        'Compare last digits of reference (from the right)'
                                                    }
                                                />
                                                <div className={`${styles.field} ${styles.fieldNarrow}`}>
                                                    <span className={styles.fieldLabel} dir={direction}>
                                                        {t('ReferenceLastDigits') || 'Last digits'}
                                                    </span>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        variant="standard"
                                                        value={lastDigits}
                                                        disabled={!useLastDigits}
                                                        onChange={(e) => setLastDigits(clampLastDigits(e.target.value))}
                                                        inputProps={{ min: MIN_LAST_DIGITS, max: MAX_LAST_DIGITS }}
                                                        className={fieldStyles.styleInput}
                                                        InputProps={comboInputProps}
                                                    />
                                                </div>
                                                {useLastDigits && (
                                                    <p className={styles.fieldHint}>
                                                        {(t('CompareReferenceLastDigitsHint') ||
                                                            'References are matched by their last {{n}} digits from the right.').replace(
                                                            '{{n}}',
                                                            String(lastDigits)
                                                        )}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>

                                {smartMode && (
                                    <p className={styles.hint}>
                                        {t('DuplicateSmartHint') ||
                                            'The reference is ignored so the same transaction imported from Finsite and from Xtram can be found. These are possible duplicates — review each group before deleting. Only near-identical groups are pre-selected.'}
                                    </p>
                                )}

                                {apiError && (
                                    <p className={styles.errorBox}>
                                        {t('ErrorFetchingDuplicates') || apiError}
                                    </p>
                                )}

                                {apiLoading ? (
                                    <div className={styles.loading}>
                                        <CircularProgress className={styles.spinner} size={36} />
                                    </div>
                                ) : duplicateGroups.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        {t('NoDuplicatesFound') || 'No duplicate entries found.'}
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.chips}>
                                            <span className={styles.chip}>
                                                {`${t('DuplicateGroups') || 'Duplicate groups'}: ${duplicateGroups.length}`}
                                            </span>
                                            <span className={styles.chip}>
                                                {`${t('DuplicateRows') || 'Duplicate rows'}: ${totalDuplicateRows}`}
                                            </span>
                                            <span className={`${styles.chip} ${styles.chipRed}`}>
                                                {`${t('SelectedForDeletion') || 'Selected for deletion'}: ${selectedCount}`}
                                            </span>
                                            {!smartMode && useLastDigits && (
                                                <span className={styles.chip}>
                                                    {`${t('ReferenceLastDigits') || 'Last digits'}: ${lastDigits}`}
                                                </span>
                                            )}
                                        </div>

                                        {duplicateGroups.map((group, groupIdx) => (
                                            <div key={groupIdx} className={styles.group}>
                                                <div className={styles.groupHeader}>
                                                    <p className={styles.sectionTitle}>
                                                        {`${t('Group') || 'Group'} ${groupIdx + 1} (${group.rows.length})`}
                                                    </p>
                                                    {smartMode && (
                                                        <span className={`${styles.chip}${group.confidence >= CONFIDENT_SIMILARITY ? '' : ` ${styles.chipOrange}`}`}>
                                                            {`${t('MatchConfidence') || 'Match'}: ${Math.round(group.confidence * 100)}%`}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={styles.tableWrap}>
                                                    <TableContainer component={Paper} variant="outlined">
                                                        <Table size="small" dir={direction}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell padding="checkbox" />
                                                                    {columns.map((col) => (
                                                                        <TableCell key={col.key}>
                                                                            {col.label}
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {group.rows.map((row) => {
                                                                    const checked = !!selectedIds[row.id];
                                                                    return (
                                                                        <TableRow
                                                                            key={row.id}
                                                                            hover
                                                                            className={checked ? styles.rowSelected : undefined}
                                                                        >
                                                                            <TableCell padding="checkbox">
                                                                                <Tooltip
                                                                                    title={
                                                                                        checked
                                                                                            ? t('WillBeDeleted') || 'Will be deleted'
                                                                                            : t('WillBeKept') || 'Will be kept'
                                                                                    }
                                                                                >
                                                                                    <Checkbox
                                                                                        checked={checked}
                                                                                        onChange={() => toggleRow(row.id)}
                                                                                        sx={deleteCheckboxSx}
                                                                                    />
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                            {columns.map((col) => (
                                                                                <TableCell key={col.key}>
                                                                                    {col.render ? col.render(row) : (row[col.key] ?? '')}
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>

                            <div className={styles.footer}>
                                <p className={styles.footerHint}>
                                    {t('CheckedRowsWillBeDeleted') || 'Checked rows will be deleted, unchecked rows will be kept.'}
                                </p>
                                <button
                                    type="button"
                                    className={styles.secondaryBtn}
                                    onClick={onClose}
                                    disabled={busy}
                                >
                                    {t('Cancel') || 'Cancel'}
                                </button>
                                <button
                                    type="button"
                                    className={styles.dangerBtn}
                                    onClick={handleDelete}
                                    disabled={busy || selectedCount === 0}
                                >
                                    {busy
                                        ? <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                        : <DeleteIcon fontSize="small" />}
                                    {`${t('DeleteSelected') || 'Delete selected'} (${selectedCount})`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default DuplicateEntriesModal;
export { normalizeDate, normalizeAmount, normalizeText, normalizeReference };
